import React from 'react';
import { Container, Grid, Typography } from '@mui/material';
import { useTheme } from '../context/ThemeContext';
import BubbleChartOutlined from '@mui/icons-material/BubbleChartOutlined';
import VacuumIcon from '../assets/images/our-services/vacuum.png';
import DryingIcon from '../assets/images/our-services/drying.png';
import BrushIcon from '../assets/images/our-services/brush.png';
import OurServiceBg from '../assets/images/header-backgrounds/background3.png';
import HeroHeader from '../components/HeroHeader';
import {
  Section,
  SectionTitle,
  ProcessCard,
  ProcessStep
} from '../components/styled/CommonStyles';

function ServicesPage() {
  const { currentTheme } = useTheme();

  return (
    <>
      <HeroHeader 
        title="Our Service" 
        backgroundImage={OurServiceBg}
      />

      <Section theme={currentTheme}>
        <Container>
          <SectionTitle theme={currentTheme}>Carpet Cleaning Process</SectionTitle>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <ProcessCard theme={currentTheme}>
                <ProcessStep theme={currentTheme}>
                  <Typography variant="h5" component="h3" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BubbleChartOutlined sx={{ fontSize: 36 }} />
                    Pre-treatment
                  </Typography>
                  <Typography>
                    Before we begin the cleaning process, we apply two different pre-treatments to your carpets to loosen and dissolve any embedded dirt or grime. Our second pre-treatment also includes a deodorizer to leave your carpets smelling fresh and clean.
                  </Typography>
                </ProcessStep>
              </ProcessCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <ProcessCard theme={currentTheme}>
                <ProcessStep theme={currentTheme}>
                  <Typography variant="h5" component="h3" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img src={BrushIcon} alt="Counter Rotating Brush" style={{ width: 36, height: 36 }} />
                    Counter Rotating Brush Cleaning
                  </Typography>
                  <Typography>
                    Next, we use a machine with a counter rotating brush (CRB) to deeply scrub and extract the dirt and grime from the carpet fibers. This powerful tool is designed to effectively remove even the toughest stains and leave your carpets looking like new.
                  </Typography>
                </ProcessStep>
              </ProcessCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <ProcessCard theme={currentTheme}>
                <ProcessStep theme={currentTheme}>
                  <Typography variant="h5" component="h3" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img src={VacuumIcon} alt="Vacuuming" style={{ width: 36, height: 36 }} />
                    Vacuuming
                  </Typography>
                  <Typography>
                    Once the carpet is completely dry, we recommend vacuuming to remove any remaining debris and ensure a clean finish. We pride ourselves on our attention to detail and thorough cleaning process.
                  </Typography>
                </ProcessStep>
              </ProcessCard>
            </Grid>
          </Grid>
        </Container>
      </Section>

      <Section theme={currentTheme} sx={{ bgcolor: 'background.default' }}>
        <Container>
          <SectionTitle theme={currentTheme}>Tile and Grout Cleaning Process</SectionTitle>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <ProcessCard theme={currentTheme}>
                <ProcessStep theme={currentTheme}>
                  <Typography variant="h5" component="h3" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BubbleChartOutlined sx={{ fontSize: 36 }} />
                    Pre-treatment
                  </Typography>
                  <Typography>
                    Before we begin the cleaning process, we apply a pre-treatment to your tiles and grout to loosen and dissolve any embedded dirt or grime.
                  </Typography>
                </ProcessStep>
              </ProcessCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <ProcessCard theme={currentTheme}>
                <ProcessStep theme={currentTheme}>
                  <Typography variant="h5" component="h3" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img src={BrushIcon} alt="Counter Rotating Brush" style={{ width: 36, height: 36 }} />
                    Counter Rotating Brush Cleaning
                  </Typography>
                  <Typography>
                    Next, we use our CRB machine to deeply scrub and extract the dirt and grime from the surface of your tiles and grout. The counter rotating brush bristles are able to reach into the pores of the grout to remove stubborn stains and leave a clean, refreshed finish.
                  </Typography>
                </ProcessStep>
              </ProcessCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <ProcessCard theme={currentTheme}>
                <ProcessStep theme={currentTheme}>
                  <Typography variant="h5" component="h3" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img src={DryingIcon} alt="Drying" style={{ width: 36, height: 36 }} />
                    Drying
                  </Typography>
                  <Typography>
                    After the CRB cleaning, we remove all moisture from the surface of your tiles and grout to ensure they are completely dry when the cleaning process is complete. Our advanced cleaning technology allows for quick drying times, so you can get back to your daily routine as soon as possible.
                  </Typography>
                </ProcessStep>
              </ProcessCard>
            </Grid>
          </Grid>
        </Container>
      </Section>

      <Section theme={currentTheme}>
        <Container>
          <SectionTitle theme={currentTheme}>Upholstery Cleaning Process</SectionTitle>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <ProcessCard theme={currentTheme}>
                <ProcessStep theme={currentTheme}>
                  <Typography variant="h5" component="h3" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BubbleChartOutlined sx={{ fontSize: 36 }} />
                    Pre-treatment
                  </Typography>
                  <Typography>
                    Before we begin the cleaning process, we apply a pre-treatment to your upholstery to loosen and dissolve any embedded dirt or grime.
                  </Typography>
                </ProcessStep>
              </ProcessCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <ProcessCard theme={currentTheme}>
                <ProcessStep theme={currentTheme}>
                  <Typography variant="h5" component="h3" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img src={BrushIcon} alt="Counter Rotating Brush" style={{ width: 36, height: 36 }} />
                    Deep Cleaning
                  </Typography>
                  <Typography>
                    We use specialized equipment and cleaning solutions to deep clean your upholstery, removing stains and odors while preserving the fabric's integrity.
                  </Typography>
                </ProcessStep>
              </ProcessCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <ProcessCard theme={currentTheme}>
                <ProcessStep theme={currentTheme}>
                  <Typography variant="h5" component="h3" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img src={DryingIcon} alt="Drying" style={{ width: 36, height: 36 }} />
                    Drying
                  </Typography>
                  <Typography>
                    Our advanced cleaning methods ensure quick drying times, allowing you to use your furniture again in no time. We also provide fabric protection options to help maintain your upholstery's appearance.
                  </Typography>
                </ProcessStep>
              </ProcessCard>
            </Grid>
          </Grid>
        </Container>
      </Section>
    </>
  );
}

export default ServicesPage;
   